import React, { useEffect, useState, memo } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import axios from "axios";
import CONFIG from "../config";
import { useBranding } from "../context/BrandingContext";
import SEO from "../components/common/SEO";

const Terms = memo(() => {
  const { t, i18n } = useTranslation();
  const { branding } = useBranding();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);

  const isArabic = i18n.language?.startsWith('ar');

  useEffect(() => {
    const fetchPage = async () => {
      try {
        const res = await axios.get(`${CONFIG.API_URL}/api/legal/terms`);
        setPage(res.data);
      } catch (err) {
        console.error("Failed to fetch terms:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchPage();
  }, []);

  if (loading) {
    return <div className="text-center py-40"><i className="fas fa-spinner fa-spin text-4xl text-blue-600"></i></div>;
  }

  const title = isArabic ? (page?.title_ar || t('pages.terms.title')) : (page?.title || t('pages.terms.title'));
  const content = isArabic ? (page?.content_ar || page?.content) : (page?.content);
  const contactEmail = page?.email_override || branding?.contact?.email || "support@luxehome.shop";

  return (
    <>
      <Helmet>
        <html lang={isArabic ? "ar" : "en"} dir={isArabic ? "rtl" : "ltr"} />
        <title>{title}</title>
      </Helmet>
      <SEO />

      <section className="max-w-4xl mx-auto py-24 px-6" style={{ color: 'var(--text-main)' }}>
        <h1 className="text-4xl font-black mb-8 border-b-4 w-fit pb-2" style={{ color: 'var(--text-header)', borderColor: 'var(--primary)' }}>
          {title}
        </h1>

        {content ? (
          <div
            className="prose prose-lg max-w-none font-medium leading-relaxed"
            style={{ color: 'var(--text-main)' }}
            dangerouslySetInnerHTML={{ __html: content }}
          />
        ) : (
          <div className="space-y-6">
            <p className="mb-4">{t('pages.terms.intro')}</p>
            <h2 className="text-xl font-semibold mt-6 mb-2">{t('pages.terms.use_title')}</h2>
            <p className="mb-4">{t('pages.terms.use_text')}</p>
            <h2 className="text-xl font-semibold mt-6 mb-2">{t('pages.terms.product_title')}</h2>
            <p className="mb-4">{t('pages.terms.product_text')}</p>
          </div>
        )}

        <div className="mt-12 p-8 bg-blue-50 rounded-[30px] border border-blue-100 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="text-center md:text-left">
            <h3 className="text-lg font-black mb-1" style={{ color: 'var(--text-header)' }}>
              {isArabic ? (page?.help_title_ar || t('pages.terms.questions', 'Have questions?')) : (page?.help_title || t('pages.terms.questions', 'Have questions?'))}
            </h3>
            <p className="text-gray-600 font-medium">
              {isArabic ? (page?.help_desc_ar || t('pages.terms.contact_desc', 'Contact us for any clarifications about our terms.')) : (page?.help_desc || t('pages.terms.contact_desc', 'Contact us for any clarifications about our terms.'))}
            </p>
          </div>
          <a href={`mailto:${contactEmail}`} className="bg-blue-600 text-white px-8 py-3 rounded-2xl font-black text-sm uppercase tracking-widest hover:scale-105 active:scale-95 transition-all shadow-lg shadow-blue-600/20">
            {contactEmail}
          </a>
        </div>
      </section>
    </>
  );
});

export default Terms;
